import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HashRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import apiConfig from "./config/api";
import RoleSelection from "./pages/RoleSelection";
import RoleChangeConfirmation from "./pages/RoleChangeConfirmation";
import SignOutConfirmation from "./pages/SignOutConfirmation";
import NotFound from "./pages/NotFound";
// Phase 2: Web app pages
import SignInPage from "./pages/SignInPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import AppRouterPage from "./pages/AppRouterPage";
import AdminPage from "./pages/AdminPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminGroupsPage from "./pages/AdminGroupsPage";
import AdminDomainsPage from "./pages/AdminDomainsPage";
import AdminImpersonatePage from "./pages/AdminImpersonatePage";
import AdminFeedbackPage from "./pages/AdminFeedbackPage";
import AdminAiAssistPage from "./pages/AdminAiAssistPage";
import SettingsPage from "./pages/SettingsPage";
import LoginLandingPage from "./pages/LoginLandingPage";
import NewsletterPage from "./pages/NewsletterPage";
import TeacherPolicySettingsPage from "./pages/TeacherPolicySettingsPage";
import FeedbackModalHost from "./components/FeedbackModalHost";
import { openFeedbackModal } from "./utils/feedbackModal";
import { consumePendingFeedbackIntent } from "./utils/feedbackDeepLink";
import { ensureFreshAccessToken, silentRefreshAccessToken } from "./utils/silentAuthRefresh";

const queryClient = new QueryClient();

// Helper function to check localStorage for auth
const checkAuthFromStorage = () => {
  const savedToken = localStorage.getItem('trafficLiteToken');
  const savedUser = localStorage.getItem('trafficLiteUser');
  
  if (savedToken && savedUser) {
    try {
      return {
        token: savedToken,
        user: JSON.parse(savedUser)
      };
    } catch (error) {
      console.error('Failed to parse saved user data:', error);
      localStorage.removeItem('trafficLiteToken');
      localStorage.removeItem('trafficLiteUser');
      return null;
    }
  }
  return null;
};

const syncAuthToExtension = (syncToken: string | null, syncUser: any, attempts = 3) => {
  if (!syncToken || !syncUser) return;

  const profile = {
    email: syncUser.email,
    role: syncUser.role || 'teacher',
    name: syncUser.name || '',
    isAdmin: Boolean(syncUser.isAdmin),
    isSuperAdmin: Boolean(syncUser.isSuperAdmin),
    id: syncUser.id
  };

  for (let i = 0; i < attempts; i++) {
    window.setTimeout(() => {
      try {
        window.postMessage(
          {
            source: 'trafficlite-web',
            action: 'SYNC_AUTH_TOKEN',
            token: syncToken,
            profile
          },
          window.location.origin
        );
      } catch (error) {
        console.warn('Auth sync postMessage failed:', error);
      }
    }, i * 300);
  }
};

const getDefaultRouteForUser = (appUser: any) => {
  if (!appUser) return '/signin';
  if (appUser.isSuperAdmin) return '/admin/users';
  if (appUser.isAdmin) return '/admin/settings';
  return '/teacher/policy-settings';
};

// Component to handle /dashboard route with localStorage fallback (fixes double sign-in issue)
const DashboardRoute = ({ user: userState, setUser, setToken }) => {
  const [refreshingUser, setRefreshingUser] = useState(false);

  // If user state is null but localStorage has auth, check and update state
  useEffect(() => {
    if (!userState) {
      const auth = checkAuthFromStorage();
      if (auth) {
        console.log('🔄 Dashboard route: Re-checking auth - found user in localStorage:', auth.user.email);
        setToken(auth.token);
        setUser(auth.user);
      }
    }
  }, [userState, setUser, setToken]);

  // Fetch fresh user from /auth/me so role changes (e.g. admin→teacher) take effect immediately
  useEffect(() => {
    let cancelled = false;

    const refreshUser = async () => {
      const initialToken = localStorage.getItem('trafficLiteToken');
      if (!initialToken) return;

      setRefreshingUser(true);
      try {
        const ensured = await ensureFreshAccessToken({ token: initialToken });
        let token = ensured || initialToken;
        const meUrl = `${apiConfig.API_BASE_URL}/auth/me`;

        let response = await fetch(meUrl, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.status === 401) {
          const refreshed = await silentRefreshAccessToken({ force: true, token });
          if (refreshed.ok && refreshed.token) {
            token = refreshed.token;
            response = await fetch(meUrl, {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
          }
        }

        if (cancelled) return;

        if (response.ok) {
          const userData = await response.json();
          const freshUser = {
            email: userData.email,
            role: userData.role,
            name: userData.name,
            isAdmin: userData.isAdmin,
            isSuperAdmin: userData.isSuperAdmin,
            id: userData.id
          };
          localStorage.setItem('trafficLiteUser', JSON.stringify(freshUser));
          setUser(freshUser);
          setToken(token);
          console.log('🔄 Dashboard: Refreshed user from /auth/me:', freshUser.email, 'role:', freshUser.role);
        }
      } catch (err) {
        console.warn('Dashboard: Could not refresh user from /auth/me, using cached:', err);
      } finally {
        if (!cancelled) setRefreshingUser(false);
      }
    };

    refreshUser();
    return () => {
      cancelled = true;
    };
  }, [setUser, setToken]);

  // Use user state if available, otherwise check localStorage directly
  const effectiveUser = userState || checkAuthFromStorage()?.user;

  if (refreshingUser) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your account...</p>
        </div>
      </div>
    );
  }

  if (effectiveUser) {
    return <Navigate to={getDefaultRouteForUser(effectiveUser)} replace />;
  }

  return <Navigate to="/signin" />;
};

const FEEDBACK_DEEPLINK_BLOCKED_PATHS = new Set(['/app', '/signin', '/auth/callback', '/login-landing', '/newsletter']);

const FeedbackDeepLinkIntentConsumer = ({ isLoading, user }: { isLoading: boolean; user: any }) => {
  const location = useLocation();

  useEffect(() => {
    if (isLoading) return;
    const effectiveUser = user || checkAuthFromStorage()?.user;
    if (!effectiveUser) return;
    if (effectiveUser.isSuperAdmin) return;
    if (FEEDBACK_DEEPLINK_BLOCKED_PATHS.has(location.pathname)) return;

    const pendingIntent = consumePendingFeedbackIntent();
    if (!pendingIntent) return;

    const fallbackReturnRoute = effectiveUser.isAdmin ? '/admin/settings' : '/teacher/policy-settings';
    openFeedbackModal({
      initialStep: 'main',
      returnToRoute: pendingIntent.returnToRoute || fallbackReturnRoute,
      source: 'manual'
    });
  }, [isLoading, location.pathname, user]);

  return null;
};

const App = () => {
  console.log('🚦 App component rendering...');
  
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const feedbackPromptCheckRequestedRef = useRef(false);

  useEffect(() => {
    console.log('🔍 Checking for authentication...');
    
    // Check for URL parameters in hash (from Chrome extension)
    const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const hashToken = hashParams.get('token');
    const hashEmail = hashParams.get('email');
    
    // Check for URL parameters in regular URL (from web app OAuth)
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    const urlEmail = urlParams.get('email');
    
    const token = hashToken || urlToken;
    const email = hashEmail || urlEmail;
    
    console.log('🔗 URL parameters:', { 
      hashToken: hashToken ? 'PRESENT' : 'NOT PRESENT', 
      hashEmail: hashEmail,
      urlToken: urlToken ? 'PRESENT' : 'NOT PRESENT', 
      urlEmail: urlEmail,
      finalToken: token ? 'PRESENT' : 'NOT PRESENT',
      finalEmail: email
    });
    
    if (token && email) {
      console.log('✅ Found authentication in URL parameters');
      // Use the token from URL parameters
      setToken(token);
      setUser({ email: email, role: 'teacher' }); // Assume teacher for now
      
      // Save to localStorage for future use
      localStorage.setItem('trafficLiteToken', token);
      const urlUser = { email: email, role: 'teacher', isAdmin: false, isSuperAdmin: false };
      localStorage.setItem('trafficLiteUser', JSON.stringify(urlUser));
      syncAuthToExtension(token, urlUser);
      
      // Re-route through role resolver so admins land on Policy Details by default.
      window.location.href =
        window.location.origin + window.location.pathname + '#/app';
      
      setIsLoading(false);
      return;
    }
    
    // Fallback to localStorage
    const auth = checkAuthFromStorage();
    if (auth) {
        console.log('✅ Found authentication in localStorage');
      setToken(auth.token);
      setUser(auth.user);
      syncAuthToExtension(auth.token, auth.user);
    } else {
      console.log('❌ No authentication found');
    }
    
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (isLoading) return;
    if (feedbackPromptCheckRequestedRef.current) return;
    const effectiveUser = user || checkAuthFromStorage()?.user;
    if (!effectiveUser) return;
    if (effectiveUser.isSuperAdmin) return;
    if (!effectiveUser.isAdmin && effectiveUser.role !== 'teacher') return;

    feedbackPromptCheckRequestedRef.current = true;

    const handleBridgeResult = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as {
        source?: string;
        action?: string;
        success?: boolean;
        shouldOpen?: boolean;
      };
      if (data?.source !== 'trafficlite-extension' || data?.action !== 'FEEDBACK_PROMPT_CHECK_RESULT') {
        return;
      }
      if (data.success && data.shouldOpen) {
        openFeedbackModal({ source: 'auto' });
      }
    };

    window.addEventListener('message', handleBridgeResult);
    const timer = window.setTimeout(() => {
      try {
        window.postMessage(
          {
            source: 'trafficlite-web',
            action: 'FEEDBACK_PROMPT_CHECK'
          },
          window.location.origin
        );
      } catch (error) {
        console.warn('Feedback prompt check postMessage failed:', error);
      }
    }, 500);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('message', handleBridgeResult);
    };
  }, [isLoading, user]);

  const handleLogin = (newToken, userData) => {
    setToken(newToken);
    setUser(userData);
    localStorage.setItem('trafficLiteToken', newToken);
    localStorage.setItem('trafficLiteUser', JSON.stringify(userData));
    syncAuthToExtension(newToken, userData);
  };

  const handleLogout = () => {
    feedbackPromptCheckRequestedRef.current = false;
    setToken(null);
    setUser(null);
    localStorage.removeItem('trafficLiteToken');
    localStorage.removeItem('trafficLiteUser');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading Traffic Lite...</p>
        </div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <FeedbackModalHost />
        <Toaster />
        <Sonner />
        <HashRouter>
          <FeedbackDeepLinkIntentConsumer isLoading={isLoading} user={user} />
          <Routes>
            {/* Phase 2: Web app authentication and routing */}
            <Route path="/signin" element={<SignInPage />} />
            <Route path="/newsletter" element={<NewsletterPage />} />
            <Route path="/login-landing" element={<LoginLandingPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/app" element={<AppRouterPage />} />
            <Route path="/teacher" element={<Navigate to="/teacher/policy-settings" replace />} />
            <Route path="/teacher/policy-settings" element={<TeacherPolicySettingsPage />} />
            <Route path="/teacher/data-dashboard" element={<Navigate to="/teacher/policy-settings" replace />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/settings" element={<SettingsPage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/groups" element={<AdminGroupsPage />} />
            <Route path="/admin/domains" element={<AdminDomainsPage />} />
            <Route path="/admin/feedback" element={<AdminFeedbackPage />} />
            <Route path="/admin/ai-assist" element={<AdminAiAssistPage />} />
            <Route path="/admin/impersonate" element={<AdminImpersonatePage />} />
            
            {/* Public routes */}
            <Route
              path="/"
              element={
                user ? (
                  <Navigate to={getDefaultRouteForUser(user)} replace />
                ) : (
                  <Navigate to="/signin" replace />
                )
              }
            />
            
            {/* Protected routes */}
            <Route 
              path="/dashboard" 
              element={<DashboardRoute user={user} setUser={setUser} setToken={setToken} />}
            />
            
            <Route path="/role-selection" element={<RoleSelection />} />
            <Route path="/role-change" element={<RoleChangeConfirmation newRole="teacher" />} />
            <Route path="/signout-confirmation" element={<SignOutConfirmation />} />
            
            {/* Catch-all route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </HashRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
